/**
 * Organization Loader Hook
 *
 * Manages organization loading, validation, and associated user setup.
 * Handles Redux state updates, internationalization, and analytics initialization.
 * Extracted from UserRequiredContainer for better separation of concerns.
 *
 * @module useOrganisationLoader
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useLazyGetOrganisationQuery } from '@/app/services/organisation'
import { useLazyGetOrganisationUserQuery } from '@/app/services/organisationUsers'
import { useLazyGetUserOrganisationsQuery } from '@/app/services/user'
import { setLanguage, setLocale, setUserRole } from '@/app/slices/appSlice'
import { setLastPaymentStatus, setOrganisation } from '@/app/slices/organisationSlice'
import { getLanguageToUse, getLocaleToUse, LOADING_STEPS } from '@/common/utils'
import { NoOrganisationError, OrganisationLoadError } from '@/common/utils/AuthErrors'
import { validateOrganisationAccess } from '@/common/utils/subscriptionUtils'

import useLoadingContext from './useLoadingContext'

/**
 * Organization loader hook for managing organization context setup.
 *
 * Handles the organization loading sequence:
 * 1. Organization data loading and validation (blocking)
 * 2. Redux state updates for org, locale, language
 * 3. Background loading of user-organization data (non-blocking)
 *
 * @returns {{loadOrganisationContext: Function, loadOrganisation: Function}} Organization loading functions
 */
export const useOrganisationLoader = () => {
    const dispatch = useDispatch()
    const { i18n } = useTranslation()

    const [getOrganisation] = useLazyGetOrganisationQuery()
    const [getOrganisationUser] = useLazyGetOrganisationUserQuery()
    const [getUserOrganisations] = useLazyGetUserOrganisationsQuery()

    const { setStepLoading, setStepSuccess } = useLoadingContext()

    /**
     * Loads organization data and validates access.
     *
     * @param {string} organisationId - Organization ID to load
     * @returns {Promise<Object>} Organization data from API
     * @throws {OrganisationLoadError} If organization loading fails
     * @throws {NoOrganisationError} If organization access is invalid
     */
    const loadOrganisation = useCallback(
        async (organisationId) => {
            try {
                console.debug('[Org Loader] Loading organization')
                const organisationResult = await getOrganisation({ organisationId }).unwrap()

                // Validate organization access
                validateOrganisationAccess(organisationResult)

                console.debug('[Org Loader] Organization loaded successfully:', organisationResult.name)
                return organisationResult
            } catch (error) {
                console.error('[Org Loader] Failed to load organization:', error)

                // Handle specific error cases
                if ([400, 404].includes(error.status)) {
                    throw new NoOrganisationError(error)
                }

                throw new OrganisationLoadError(organisationId, error)
            }
        },
        [getOrganisation]
    )

    /**
     * Starts background loading of user-organization data (fire-and-forget).
     *
     * Triggers RTK Query to cache the data for later use without blocking app initialization.
     * Uses default role ('user') until data loads - admin pages handle this gracefully.
     *
     * @param {Object} user - User object with userId
     * @param {string} organisationId - Organization ID
     */
    const startUserOrganisationLoading = useCallback(
        (user, organisationId) => {
            console.debug('[Org Loader] Starting background user-organization data loading')

            // Fire-and-forget: trigger queries for RTK Query to cache
            // These don't block - role defaults to 'user' until loaded
            getOrganisationUser({
                organisationId,
                userId: user.userId,
            })
                .unwrap()
                .then((result) => {
                    console.debug('[Org Loader] Organization user loaded, role:', result?.role)
                    // Update Redux with actual role
                    dispatch(setUserRole(result?.role?.toLowerCase() || 'user'))
                })
                .catch((error) => {
                    console.warn('[Org Loader] Failed to load organization user (non-blocking):', error)
                })

            getUserOrganisations({ userId: user.userId })
        },
        [dispatch, getOrganisationUser, getUserOrganisations]
    )

    /**
     * Updates application state with organization and user data.
     * Uses batched updates for optimal performance.
     *
     * Note: User role is set to default 'user' here and updated asynchronously
     * when getOrganisationUser completes in the background.
     *
     * @param {Object} params - State update parameters
     * @param {Object} params.user - User object
     * @param {Object} params.organisation - Organization object
     * @param {Object} params.userContext - User context with payment information
     */
    const updateApplicationState = useCallback(
        ({ organisation, user, userContext }) => {
            console.debug('[Org Loader] Updating application state with organization data')

            // Calculate derived state
            const lastPaymentStatus = userContext.lastPayment?.status?.toLowerCase() || 'unknown'
            const localeToUse = getLocaleToUse(organisation)
            const languageToUse = getLanguageToUse(user, organisation)

            // Update internationalization
            i18n.changeLanguage(languageToUse)

            dispatch(setOrganisation(organisation))
            dispatch(setLastPaymentStatus(lastPaymentStatus))
            dispatch(setLanguage(languageToUse))
            dispatch(setLocale(localeToUse))

            console.debug('[Org Loader] Application state updated successfully')
        },
        [dispatch, i18n]
    )

    /**
     * Complete organization context loading and setup.
     *
     * Orchestrates the organization loading sequence:
     * 1. Loads organization data and validates access (blocking)
     * 2. Updates application state with Redux updates
     * 3. Starts background loading of user-organization data (non-blocking)
     *
     * User role defaults to 'user' and updates asynchronously when background
     * loading completes. Admin pages handle this gracefully via RoleRequiredContainer.
     *
     * @param {Object} user - User object from authentication
     * @param {string} selectedOrganisationId - Organization ID to load
     * @param {Object} userContext - User context with additional metadata
     * @returns {Promise<{organisation: Object}>} Organization setup result
     * @throws {OrganisationLoadError} If organization loading fails
     */
    const loadOrganisationContext = useCallback(
        async (user, selectedOrganisationId, userContext) => {
            try {
                console.debug('[Org Loader] Starting organization context loading')

                // Step 1: Load organization data (blocking)
                setStepLoading(LOADING_STEPS.ORGANISATION)

                const organisation = await loadOrganisation(selectedOrganisationId)

                setStepSuccess(LOADING_STEPS.ORGANISATION)

                // Step 2: Update application state
                updateApplicationState({
                    user,
                    organisation,
                    userContext,
                })

                // Step 3: Start background loading of user-organization data (non-blocking)
                startUserOrganisationLoading(user, selectedOrganisationId)

                console.debug('[Org Loader] Organization context loading completed successfully')

                return {
                    organisation,
                }
            } catch (error) {
                console.error('[Org Loader] Organization context loading failed:', error)
                throw error // Re-throw to maintain error type for useUserInitialization to handle
            }
        },
        [loadOrganisation, startUserOrganisationLoading, updateApplicationState, setStepLoading, setStepSuccess]
    )

    return {
        loadOrganisationContext,
        loadOrganisation,
    }
}
